import { useQuery } from '@tanstack/react-query';
import { Check, CreditCard, Minus, Sparkles, TriangleAlert } from 'lucide-react';
import { useState, type FormEvent, type ReactNode } from 'react';
import { Link } from 'react-router';
import { Logo } from '../components/Layout';
import { Alert, Button, cn, ErrorState, Field, Input, PageLoader, Select } from '../components/ui';
import { api, ApiError, errorMessage } from '../lib/api';
import { useAuth } from '../lib/auth';
import { formatPrice, SALES_EMAIL, type BillingFeature, type CatalogItem } from '../lib/billing';
import { formatDate } from '../lib/format';
import { useDebouncedValue } from '../lib/hooks';

interface SignUpOptions {
  enabled: boolean;
  salesEmail: string;
  trialDays?: number;
  retentionDays?: number;
  /** Schools can subscribe online. */
  subscribe?: boolean;
  catalog?: CatalogItem[];
}
interface SignUpResult {
  school: string;
  url: string;
}
type Plan = 'trial' | 'subscribe';

const DAY = 86_400_000;
const TIME_ZONES: [string, string][] = [
  ['America/New_York', 'Eastern'],
  ['America/Chicago', 'Central'],
  ['America/Denver', 'Mountain'],
  ['America/Phoenix', 'Arizona'],
  ['America/Los_Angeles', 'Pacific'],
  ['America/Anchorage', 'Alaska'],
  ['Pacific/Honolulu', 'Hawaii'],
  ['Europe/London', 'London'],
  ['UTC', 'UTC'],
];
const COMBINING_MARKS = new RegExp(`[${String.fromCharCode(0x300)}-${String.fromCharCode(0x36f)}]`, 'g');
const toAddress = (name: string) =>
  name.toLowerCase().normalize('NFKD').replace(COMBINING_MARKS, '').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 48);
const guessTimeZone = () => {
  const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return TIME_ZONES.some(([value]) => value === zone) ? zone : 'America/New_York';
};
/** Server field paths, as the form's field names. */
const FIELD_FOR: Record<string, string> = {
  'school.name': 'name',
  'school.slug': 'slug',
  'school.timezone': 'timezone',
  'admin.name': 'adminName',
  'admin.email': 'email',
  'admin.password': 'password',
  acceptDeletion: 'accept',
};

const TRIAL_INCLUDES = [
  'Assets, checkouts, loaners, and the technology help desk',
  'The Facilities module: maintenance requests, schedules, inspections, and equipment',
  'Every integration, with help desk and facilities email checked automatically',
];
const TRIAL_EXCLUDES = [
  'scheduled roster and device syncs (click Sync now instead)',
  'Google Workspace Provisioning and Blackbaud Billing Management',
];
const PLAN_INCLUDES = [
  'Assets, checkouts, loaners, and the technology help desk',
  'Every integration, synced on the schedule you choose',
  'Add Facilities and other add-ons now or later, from Plan & billing',
];

/** The dates that matter, from today. */
function useDates(plan: Plan, options?: SignUpOptions) {
  const trialDays = options?.trialDays ?? 15;
  const retentionDays = options?.retentionDays ?? 30;
  const now = Date.now();
  const trialEnds = new Date(now + trialDays * DAY);
  const deletes = new Date((plan === 'trial' ? trialEnds.getTime() : now) + retentionDays * DAY);
  return { trialDays, retentionDays, trialEnds, deletes };
}

function Timeline({ plan, options, dark }: { plan: Plan; options?: SignUpOptions; dark?: boolean }) {
  const { trialDays, trialEnds, deletes } = useDates(plan, options);
  const steps =
    plan === 'trial'
      ? [
          { when: 'Today', text: 'Your school is ready. Try Tech and Facilities with your team.' },
          { when: formatDate(trialEnds), text: `Your ${trialDays}-day trial ends. Choose a plan to keep going.` },
          { when: formatDate(deletes), text: 'If you haven’t subscribed, your school’s account and data are deleted.', warn: true },
        ]
      : [
          { when: 'Today', text: 'Pay with Stripe, and your school opens as soon as the payment goes through.' },
          { when: formatDate(deletes), text: 'If the payment is never completed, your school’s account and data are deleted.', warn: true },
        ];
  return (
    <ol className="space-y-0">
      {steps.map((step, i) => (
        <li key={step.when} className="relative flex gap-4 pb-5 last:pb-0">
          {i < steps.length - 1 && <span className={cn('absolute top-5 bottom-0 left-[5px] w-px', dark ? 'bg-white/20' : 'bg-slate-200')} />}
          <span
            className={cn(
              'mt-1.5 size-[11px] shrink-0 rounded-full ring-4',
              step.warn ? 'bg-amber-400' : dark ? 'bg-brand-300' : 'bg-brand-600',
              dark ? 'ring-brand-950' : 'ring-white',
            )}
          />
          <div>
            <p className={cn('text-xs font-semibold tracking-wide uppercase tabular-nums', dark ? 'text-brand-300' : 'text-slate-500')}>{step.when}</p>
            <p className={cn('mt-0.5 text-sm', dark ? 'text-brand-100' : 'text-slate-700')}>{step.text}</p>
          </div>
        </li>
      ))}
    </ol>
  );
}

function Section({ title, description, children }: { title: string; description?: ReactNode; children: ReactNode }) {
  return (
    <fieldset className="space-y-4 border-t border-slate-200 pt-6">
      <div>
        <legend className="text-base font-semibold text-slate-900">{title}</legend>
        {description && <p className="mt-0.5 text-sm text-slate-500">{description}</p>}
      </div>
      {children}
    </fieldset>
  );
}

/** /sign-up: a school creates its own Edvora account, with a free trial or a subscription. */
export function SignUpPage() {
  const { me, logout } = useAuth();
  const options = useQuery({ queryKey: ['sign-up'], queryFn: () => api.get<SignUpOptions>('/sign-up'), staleTime: 60_000 });
  const [form, setForm] = useState({
    name: '',
    slug: '',
    timezone: guessTimeZone(),
    adminName: '',
    email: '',
    password: '',
    confirm: '',
    plan: 'trial' as Plan,
    addOns: [] as BillingFeature[],
    accept: false,
    website: '',
  });
  const [slugTouched, setSlugTouched] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const slug = useDebouncedValue(form.slug.trim(), 350);
  const address = useQuery({
    queryKey: ['sign-up', 'address', slug],
    queryFn: () => api.get<{ available: boolean; reason: string | null }>('/sign-up/address', { slug }),
    enabled: !!options.data?.enabled && slug.length >= 3,
    staleTime: 30_000,
  });
  const dates = useDates(form.plan, options.data);

  const update = (patch: Partial<typeof form>) => {
    setForm((current) => ({ ...current, ...patch }));
    setErrors((current) => {
      const next = { ...current };
      for (const key of Object.keys(patch)) delete next[key];
      return next;
    });
  };

  if (options.isPending) return <PageLoader />;
  if (options.error) {
    return (
      <div className="mx-auto max-w-lg px-5 py-16">
        <ErrorState error={options.error} onRetry={() => options.refetch()} />
      </div>
    );
  }
  const data = options.data;
  const salesEmail = data?.salesEmail ?? SALES_EMAIL;
  const catalog = data?.catalog ?? [];
  const platform = catalog.find((item) => item.key === 'PLATFORM');
  const monthlyAddOns = catalog.filter((item): item is CatalogItem & { key: BillingFeature } => item.key !== 'PLATFORM' && item.interval === 'month');
  const total = (platform?.amount ?? 0) + monthlyAddOns.filter((item) => form.addOns.includes(item.key)).reduce((sum, item) => sum + item.amount, 0);
  const schoolName = form.name.trim() || 'your school';

  const validate = () => {
    const found: Record<string, string> = {};
    if (form.name.trim().length < 2) found.name = 'Enter your school’s name';
    if (form.slug.trim().length < 3) found.slug = 'Use at least 3 characters';
    else if (address.data && !address.data.available && slug === form.slug.trim()) found.slug = address.data.reason ?? 'That address can’t be used';
    if (!me) {
      if (!form.adminName.trim()) found.adminName = 'Enter your name';
      if (!/^\S+@\S+\.\S+$/.test(form.email.trim())) found.email = 'Enter a valid email address';
      if (form.password.length < 10) found.password = 'Use at least 10 characters';
      else if (form.password !== form.confirm) found.confirm = 'The passwords don’t match';
    }
    if (!form.accept) found.accept = 'Confirm that you understand what happens if you don’t subscribe';
    return found;
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setFormError('');
    const found = validate();
    setErrors(found);
    if (Object.keys(found).length) return;
    setSubmitting(true);
    try {
      const result = await api.post<SignUpResult>('/sign-up', {
        school: { name: form.name.trim(), slug: form.slug.trim(), timezone: form.timezone },
        admin: me ? undefined : { name: form.adminName.trim(), email: form.email.trim(), password: form.password },
        plan: form.plan,
        addOns: form.plan === 'subscribe' ? form.addOns : [],
        acceptDeletion: form.accept,
        website: form.website || undefined,
      });
      // The school lives at its own address, so this is a full page load (or Stripe).
      window.location.assign(result.url);
    } catch (err) {
      setSubmitting(false);
      if (err instanceof ApiError) {
        const details = (err.details ?? {}) as { field?: string; code?: string };
        const fieldErrors: Record<string, string> = {};
        for (const issue of err.issues ?? []) {
          const field = FIELD_FOR[issue.path];
          if (field) fieldErrors[field] = issue.message;
        }
        if (details.field && FIELD_FOR[details.field]) fieldErrors[FIELD_FOR[details.field]] = err.message;
        if (details.code === 'ACCOUNT_EXISTS') fieldErrors.email = err.message;
        if (Object.keys(fieldErrors).length) {
          setErrors(fieldErrors);
          return;
        }
      }
      setFormError(errorMessage(err));
    }
  };

  const slugHint =
    form.slug.trim().length >= 3 && slug === form.slug.trim()
      ? address.isFetching
        ? 'Checking…'
        : address.data?.available
          ? 'Available'
          : undefined
      : 'Lowercase letters, numbers, and dashes';

  return (
    <div className="grid min-h-screen lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)]">
      <aside className="relative hidden overflow-clip bg-brand-950 lg:block">
        <div className="absolute -top-40 -right-40 size-[32rem] rounded-full bg-brand-800/40 blur-3xl" />
        <div className="sticky top-0 flex min-h-screen flex-col gap-10 p-12 text-white">
          <Link to="/login" className="flex items-center gap-3">
            <Logo className="size-10" />
            <div className="leading-tight">
              <p className="font-semibold">Edvora</p>
              <p className="text-xs font-medium tracking-wider text-brand-300 uppercase">Campus Asset</p>
            </div>
          </Link>
          <div className="max-w-md">
            <h2 className="text-4xl leading-tight font-semibold tracking-tight text-balance">
              {form.plan === 'trial' ? `Try Edvora free for ${dates.trialDays} days.` : 'Every device on campus, accounted for.'}
            </h2>
            <p className="mt-4 text-brand-200">
              {form.plan === 'trial'
                ? 'No credit card for the trial. Subscribe whenever you’re ready, from Plan & billing.'
                : 'Technology asset management for independent schools, connected to the systems you already run.'}
            </p>
            <ul className="mt-8 space-y-2.5 text-sm">
              {(form.plan === 'trial' ? TRIAL_INCLUDES : PLAN_INCLUDES).map((text) => (
                <li key={text} className="flex gap-2.5">
                  <Check className="mt-0.5 size-4 shrink-0 text-brand-300" /> {text}
                </li>
              ))}
              {form.plan === 'trial' &&
                TRIAL_EXCLUDES.map((text) => (
                  <li key={text} className="flex gap-2.5 text-brand-300/80">
                    <Minus className="mt-0.5 size-4 shrink-0" /> Not in the trial: {text}
                  </li>
                ))}
            </ul>
          </div>
          <div className="max-w-md">
            <p className="mb-4 text-xs font-semibold tracking-wider text-brand-300 uppercase">What happens next</p>
            <Timeline plan={form.plan} options={data} dark />
          </div>
          <p className="mt-auto text-xs text-brand-300/70">
            Questions or need more time? <a href={`mailto:${salesEmail}`} className="underline">{salesEmail}</a>
          </p>
        </div>
      </aside>

      <main className="px-5 py-10 sm:px-10 lg:py-16">
        <div className="mx-auto w-full max-w-xl">
          <div className="mb-8 flex items-center gap-3 lg:hidden">
            <Logo />
            <span className="font-semibold">Edvora Campus Asset</span>
          </div>

          {!data?.enabled ? (
            <div className="space-y-4">
              <h1 className="text-2xl font-semibold tracking-tight">Sign-up isn’t open yet</h1>
              <p className="text-slate-600">
                To get your school started with Edvora Campus Asset, email{' '}
                <a href={`mailto:${salesEmail}`} className="font-medium text-brand-700 underline">
                  {salesEmail}
                </a>
                .
              </p>
              <Link to="/login" className="inline-block text-sm font-medium text-brand-700 hover:underline">
                Sign in to an existing school
              </Link>
            </div>
          ) : (
            <form onSubmit={submit} noValidate className="relative space-y-8">
              <div>
                <h1 className="text-3xl font-semibold tracking-tight text-balance">Create your school’s Edvora account</h1>
                <p className="mt-2 text-slate-600">
                  Set up takes a minute. Already have one?{' '}
                  <Link to="/login" className="font-medium text-brand-700 hover:underline">
                    Sign in
                  </Link>
                </p>
              </div>
              {formError && <Alert tone="red">{formError}</Alert>}

              <Section title="Your school">
                <Field label="School name" required error={errors.name}>
                  <Input
                    id="signup-school-name"
                    value={form.name}
                    autoComplete="organization"
                    maxLength={120}
                    onChange={(e) => update({ name: e.target.value, ...(slugTouched ? {} : { slug: toAddress(e.target.value) }) })}
                  />
                </Field>
                <Field label="Web address" required error={errors.slug ?? (address.data && !address.data.available && slug === form.slug.trim() ? (address.data.reason ?? undefined) : undefined)} hint={slugHint}>
                  <div className="flex items-center rounded-lg border border-slate-300 bg-white pl-3 text-sm focus-within:border-brand-500 focus-within:ring-1 focus-within:ring-brand-500">
                    <span className="shrink-0 text-slate-400">{window.location.host}/</span>
                    <input
                      id="signup-school-address"
                      value={form.slug}
                      maxLength={48}
                      spellCheck={false}
                      onChange={(e) => {
                        setSlugTouched(true);
                        update({ slug: e.target.value.toLowerCase() });
                      }}
                      placeholder="your-school"
                      className="min-w-0 flex-1 bg-transparent py-2 pr-3 font-mono outline-none"
                    />
                  </div>
                </Field>
                <Field label="Time zone" hint="For business hours, reminders, and due dates.">
                  <Select id="signup-timezone" value={form.timezone} onChange={(e) => update({ timezone: e.target.value })}>
                    {TIME_ZONES.map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </Select>
                </Field>
              </Section>

              <Section title="Your account" description="You’ll be the school’s first administrator, and can invite your team once you’re in.">
                {me ? (
                  <Alert tone="blue">
                    You’re signed in as <strong>{me.user.name}</strong> ({me.user.email}). The new school is added to this account.{' '}
                    <button type="button" className="font-medium underline" onClick={() => void logout()}>
                      Use a different account
                    </button>
                  </Alert>
                ) : (
                  <div className="grid gap-4 sm:grid-cols-2">
                    <Field label="Your name" required error={errors.adminName} className="sm:col-span-2">
                      <Input id="signup-name" value={form.adminName} autoComplete="name" maxLength={120} onChange={(e) => update({ adminName: e.target.value })} />
                    </Field>
                    <Field label="Work email" required error={errors.email} className="sm:col-span-2" hint="If it already has an Edvora account, enter that account’s password.">
                      <Input id="signup-email" type="email" value={form.email} autoComplete="email" maxLength={254} onChange={(e) => update({ email: e.target.value })} />
                    </Field>
                    <Field label="Password" required error={errors.password} hint="At least 10 characters">
                      <Input id="signup-password" type="password" value={form.password} autoComplete="new-password" maxLength={200} onChange={(e) => update({ password: e.target.value })} />
                    </Field>
                    <Field label="Confirm password" required error={errors.confirm}>
                      <Input id="signup-confirm" type="password" value={form.confirm} autoComplete="new-password" maxLength={200} onChange={(e) => update({ confirm: e.target.value })} />
                    </Field>
                  </div>
                )}
              </Section>

              <Section title="How would you like to start?">
                <div className="grid gap-3" role="radiogroup" aria-label="How would you like to start?">
                  <PlanChoice
                    id="signup-plan-trial"
                    checked={form.plan === 'trial'}
                    onSelect={() => update({ plan: 'trial' })}
                    icon={<Sparkles />}
                    title={`Free ${dates.trialDays}-day trial`}
                    badge="No card needed"
                  >
                    Tech and Facilities for your whole team until {formatDate(dates.trialEnds)}. Help desk email is checked automatically; rosters
                    and devices sync when you click Sync now, and their schedules start once you subscribe.
                  </PlanChoice>
                  {data.subscribe && platform && (
                    <PlanChoice
                      id="signup-plan-subscribe"
                      checked={form.plan === 'subscribe'}
                      onSelect={() => update({ plan: 'subscribe' })}
                      icon={<CreditCard />}
                      title="Subscribe now"
                      badge={formatPrice(platform)}
                    >
                      Pay securely with Stripe and start right away. Choose your add-ons below; more can be added any time.
                    </PlanChoice>
                  )}
                </div>
                {form.plan === 'subscribe' && platform && (
                  <div className="rounded-lg border border-slate-200 bg-slate-50/70">
                    <ul className="divide-y divide-slate-200">
                      {monthlyAddOns.map((item) => (
                        <li key={item.key}>
                          <label className="flex cursor-pointer items-start gap-3 px-4 py-3">
                            <input
                              id={`signup-addon-${item.key}`}
                              type="checkbox"
                              className="mt-0.5 size-4 rounded border-slate-300 accent-brand-700"
                              checked={form.addOns.includes(item.key)}
                              onChange={(e) =>
                                update({ addOns: e.target.checked ? [...form.addOns, item.key] : form.addOns.filter((key) => key !== item.key) })
                              }
                            />
                            <span className="min-w-0 flex-1">
                              <span className="block text-sm font-medium text-slate-900">{item.name}</span>
                              <span className="block text-xs text-slate-500">{item.description}</span>
                            </span>
                            <span className="text-sm font-medium whitespace-nowrap text-slate-700 tabular-nums">+{formatPrice(item)}</span>
                          </label>
                        </li>
                      ))}
                    </ul>
                    <p className="flex flex-wrap items-baseline justify-between gap-2 border-t border-slate-200 px-4 py-3 text-sm text-slate-600">
                      <span>Blackbaud Billing Management ($50/year) can be added after you subscribe.</span>
                      <span>
                        Total <strong className="text-base text-slate-900 tabular-nums">{formatPrice({ ...platform, amount: total })}</strong>
                      </span>
                    </p>
                  </div>
                )}
              </Section>

              <Section title="Before you continue">
                <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-950">
                  <p className="flex gap-2 font-semibold">
                    <TriangleAlert className="mt-0.5 size-4 shrink-0 text-amber-600" />
                    Your school is deleted if you don’t subscribe
                  </p>
                  <p className="mt-2">
                    If you don’t choose and pay for an Edvora Campus Asset plan, {schoolName}’s account and all of its data (devices, people, tickets,
                    and settings) are permanently deleted{' '}
                    {form.plan === 'trial'
                      ? `${dates.retentionDays} days after your trial ends, on ${formatDate(dates.deletes)}.`
                      : `${dates.retentionDays} days after you sign up, on ${formatDate(dates.deletes)}.`}
                  </p>
                  <p className="mt-2">
                    Need more time? Email{' '}
                    <a href={`mailto:${salesEmail}`} className="font-medium underline">
                      {salesEmail}
                    </a>{' '}
                    to ask for an extension.
                  </p>
                  <div className="mt-4 border-t border-amber-200 pt-4 lg:hidden">
                    <Timeline plan={form.plan} options={data} />
                  </div>
                </div>
                <label className="flex cursor-pointer items-start gap-3 text-sm text-slate-700">
                  <input
                    id="signup-accept"
                    type="checkbox"
                    className="mt-0.5 size-4 rounded border-slate-300 accent-brand-700"
                    checked={form.accept}
                    onChange={(e) => update({ accept: e.target.checked })}
                  />
                  <span>
                    I understand that {schoolName}’s account and data will be deleted if we don’t subscribe.
                    {errors.accept && <span className="mt-1 block text-xs text-red-600">{errors.accept}</span>}
                  </span>
                </label>
              </Section>

              {/* People never see this; forms that fill it in are bots. */}
              <div aria-hidden="true" className="absolute -left-[9999px] size-px overflow-hidden">
                <label>
                  Website
                  <input tabIndex={-1} autoComplete="off" value={form.website} onChange={(e) => update({ website: e.target.value })} />
                </label>
              </div>

              <Button type="submit" variant="primary" className="w-full py-2.5" loading={submitting} icon={form.plan === 'trial' ? <Sparkles /> : <CreditCard />}>
                {form.plan === 'trial' ? 'Start free trial' : 'Continue to payment'}
              </Button>
            </form>
          )}
        </div>
      </main>
    </div>
  );
}

function PlanChoice({
  id,
  checked,
  onSelect,
  icon,
  title,
  badge,
  children,
}: {
  id: string;
  checked: boolean;
  onSelect(): void;
  icon: ReactNode;
  title: string;
  badge: string;
  children: ReactNode;
}) {
  return (
    <label
      htmlFor={id}
      className={cn(
        'flex cursor-pointer gap-3 rounded-xl border p-4 transition-colors',
        checked ? 'border-brand-500 bg-brand-50/60 ring-1 ring-brand-500' : 'border-slate-200 hover:border-slate-300',
        'has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-brand-500',
      )}
    >
      <input id={id} type="radio" name="signup-plan" className="sr-only" checked={checked} onChange={onSelect} />
      <span className={cn('mt-0.5 shrink-0 [&_svg]:size-5', checked ? 'text-brand-700' : 'text-slate-400')}>{icon}</span>
      <span className="min-w-0 flex-1">
        <span className="flex flex-wrap items-center justify-between gap-2">
          <span className="font-semibold text-slate-900">{title}</span>
          <span className="text-xs font-medium text-slate-500">{badge}</span>
        </span>
        <span className="mt-1 block text-sm text-slate-600">{children}</span>
      </span>
    </label>
  );
}
